import { legalDocumentsApi } from '../api/legalDocumentsApi';
import type {
  AdminDocumentPackageBundle,
  AdminLegalSignatureEvidence,
} from '../legalTypes';
import { getAdminApiError } from '../lib/adminApiErrors';
import {
  getLegalProfileConflictCurrent,
  mustClearSensitiveAdminState,
} from '../lib/legalAdminErrors';
import {
  applyDocumentMutationFailure,
  clearSensitiveDocumentState,
  createUploadFingerprint,
  documentMutationKey,
  requireCurrentDocument,
  supportsDocumentCommand,
  updateCanonicalPackage,
  type DocumentActions,
  type DocumentRuntime,
  type GetAdminStore,
  type SetAdminStore,
} from './legalDocumentStoreRuntime';

type ProfileActions = Pick<
  DocumentActions,
  'saveLegalProfile' | 'refreshLegalProfileConflict'
>;

const createLegalProfileMutationActions = (
  set: SetAdminStore,
  get: GetAdminStore,
  runtime: DocumentRuntime
): ProfileActions => {
  const saveLegalProfile: ProfileActions['saveLegalProfile'] = async (companyId, input) => {
    const current = get().legalProfile;
    if (!current || current.data.companyId !== companyId || current.etag.trim().length === 0) {
      const error = Object.assign(
        new Error('Refresh the selected legal profile before saving changes.'),
        { code: 'etag_required' }
      );
      set({
        documentMutationLoading: false,
        documentError: getAdminApiError(error, 'etag_required'),
        legalProfileConflict: null,
      });
      throw error;
    }
    set({ documentMutationLoading: true, documentError: null, legalProfileConflict: null });
    try {
      const saved = await legalDocumentsApi.saveLegalProfile(companyId, input, current.etag);
      set({ legalProfile: saved, documentMutationLoading: false });
      return saved;
    } catch (error) {
      const failure = getAdminApiError(error, 'legal_profile_save_failed');
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveDocumentState(set, runtime);
        set({ documentMutationLoading: false, documentError: failure });
        throw error;
      }
      const serverCurrent = getLegalProfileConflictCurrent(error);
      set({
        documentMutationLoading: false,
        documentError: failure,
        legalProfileConflict: serverCurrent
          ? { error: failure, local: current, current: serverCurrent }
          : null,
      });
      throw error;
    }
  };

  const refreshLegalProfileConflict: ProfileActions['refreshLegalProfileConflict'] = async () => {
    const companyId = get().legalProfileConflict?.local.data.companyId;
    if (!companyId) {
      return;
    }
    set({ documentDetailLoading: true });
    try {
      const current = await legalDocumentsApi.getLegalProfile(companyId);
      set({
        legalProfile: current,
        legalProfileConflict: null,
        documentDetailLoading: false,
        documentError: null,
      });
    } catch (error) {
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveDocumentState(set, runtime);
      }
      set({
        documentDetailLoading: false,
        documentError: getAdminApiError(error, 'legal_profile_refresh_failed'),
      });
      throw error;
    }
  };
  return { saveLegalProfile, refreshLegalProfileConflict };
};

type PackageCommandActions = Pick<
  DocumentActions,
  'executeDocumentCommand' | 'refreshDocumentConflict'
>;

const createPackageCommandActions = (
  set: SetAdminStore,
  get: GetAdminStore,
  runtime: DocumentRuntime
): PackageCommandActions => {
  const executeDocumentCommand: PackageCommandActions['executeDocumentCommand'] = async (command) => {
    const current = requireCurrentDocument(get);
    if (!supportsDocumentCommand(current.data, command)) {
      throw Object.assign(new Error('The server did not make this command available.'), {
        code: 'command_not_available',
      });
    }
    const key = documentMutationKey(
      runtime,
      `${current.data.id}:${command}:${current.etag}`
    );
    set({ documentMutationLoading: true, documentError: null, documentConflict: null });
    try {
      const canonical = await legalDocumentsApi.runPackageCommand(
        current.data.companyId,
        current.data.id,
        command,
        current.etag,
        key
      );
      runtime.mutationIdentity = null;
      updateCanonicalPackage(set, canonical);
      set({ documentMutationLoading: false });
      return canonical.data;
    } catch (error) {
      applyDocumentMutationFailure(set, runtime, current, error, 'document_command_failed');
      throw error;
    }
  };

  const refreshDocumentConflict: PackageCommandActions['refreshDocumentConflict'] = async () => {
    const current = get().documentConflict?.local ?? get().documentPackage?.data;
    if (!current || current.companyId === null) {
      return;
    }
    set({ documentDetailLoading: true });
    try {
      const canonical = await legalDocumentsApi.getPackage(current.companyId, current.id);
      updateCanonicalPackage(set, canonical);
      set({ documentConflict: null, documentDetailLoading: false, documentError: null });
    } catch (error) {
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveDocumentState(set, runtime);
      }
      set({
        documentDetailLoading: false,
        documentError: getAdminApiError(error, 'document_refresh_failed'),
      });
      throw error;
    }
  };
  return { executeDocumentCommand, refreshDocumentConflict };
};

type SignerCommandActions = Pick<
  DocumentActions,
  | 'configureDocumentSigners'
  | 'executeDocumentSignerCommand'
  | 'completeDocumentSigner'
>;

const createSignerCommandActions = (
  set: SetAdminStore,
  get: GetAdminStore,
  runtime: DocumentRuntime
): SignerCommandActions => {
  const configureDocumentSigners: SignerCommandActions['configureDocumentSigners'] = async (signers) => {
    const current = requireCurrentDocument(get);
    const key = documentMutationKey(
      runtime,
      `${current.data.id}:signers:${current.etag}:${JSON.stringify(signers)}`
    );
    set({ documentMutationLoading: true, documentError: null, documentConflict: null });
    try {
      const canonical = await legalDocumentsApi.configureSignerPlan(
        current.data.companyId!, current.data.id, signers, current.etag, key
      );
      runtime.mutationIdentity = null;
      updateCanonicalPackage(set, canonical);
      set({ documentMutationLoading: false });
      return canonical.data;
    } catch (error) {
      applyDocumentMutationFailure(set, runtime, current, error, 'signer_plan_failed');
      throw error;
    }
  };

  const executeDocumentSignerCommand: SignerCommandActions['executeDocumentSignerCommand'] = async (
    signerId,
    command
  ) => {
    const current = requireCurrentDocument(get);
    const key = documentMutationKey(
      runtime,
      `${current.data.id}:${signerId}:${command}:${current.etag}`
    );
    set({ documentMutationLoading: true, documentError: null, documentConflict: null });
    try {
      const canonical = await legalDocumentsApi.runSignerCommand(
        current.data.companyId!, current.data.id, signerId, command, current.etag, key
      );
      runtime.mutationIdentity = null;
      updateCanonicalPackage(set, canonical);
      set({ documentMutationLoading: false });
      return canonical.data;
    } catch (error) {
      applyDocumentMutationFailure(set, runtime, current, error, 'signer_command_failed');
      throw error;
    }
  };

  const completeDocumentSigner: SignerCommandActions['completeDocumentSigner'] = async (signerId) => {
    const current = requireCurrentDocument(get);
    const key = documentMutationKey(
      runtime,
      `${current.data.id}:${signerId}:complete:${current.etag}`
    );
    set({ documentMutationLoading: true, documentError: null, documentConflict: null });
    try {
      const canonical = await legalDocumentsApi.completeSigner(
        current.data.companyId!, current.data.id, signerId, current.etag, key
      );
      runtime.mutationIdentity = null;
      updateCanonicalPackage(set, canonical);
      set({ documentMutationLoading: false });
      return canonical.data;
    } catch (error) {
      applyDocumentMutationFailure(set, runtime, current, error, 'signer_completion_failed');
      throw error;
    }
  };

  return {
    configureDocumentSigners,
    executeDocumentSignerCommand,
    completeDocumentSigner,
  };
};

const mergeUploadedEvidence = (
  bundle: AdminDocumentPackageBundle | null,
  uploaded: AdminLegalSignatureEvidence
): AdminDocumentPackageBundle | null => {
  if (!bundle || bundle.manifest.packageId !== uploaded.packageId) {
    return bundle;
  }
  const current = bundle.evidence ?? [];
  const evidence = current.some((item) => item.id === uploaded.id)
    ? current.map((item) => item.id === uploaded.id ? uploaded : item)
    : [...current, uploaded];
  return { ...bundle, evidence };
};

type EvidenceActions = Pick<DocumentActions, 'uploadDocumentEvidence'>;

const createEvidenceMutationActions = (
  set: SetAdminStore,
  get: GetAdminStore,
  runtime: DocumentRuntime
): EvidenceActions => {
  const uploadDocumentEvidence: EvidenceActions['uploadDocumentEvidence'] = async (signerId, input) => {
    const current = requireCurrentDocument(get);
    set({
      documentMutationLoading: true,
      documentError: null,
      documentConflict: null,
    });
    try {
      const fingerprint = await createUploadFingerprint(current, signerId, input);
      const key = documentMutationKey(runtime, fingerprint);
      const evidence = await legalDocumentsApi.uploadSignedArtifact(
        current.data.companyId!, current.data.id, signerId, input, current.etag, key
      );
      runtime.mutationIdentity = null;
      set((state) => {
        const selected = state.documentPackage;
        if (selected?.data.id !== current.data.id
          || selected.data.companyId !== current.data.companyId) {
          return {};
        }
        return {
          documentBundle: mergeUploadedEvidence(state.documentBundle, evidence.data),
          documentPackage: {
            data: { ...selected.data, version: evidence.data.packageVersion },
            etag: evidence.etag,
          },
          documentMutationLoading: false,
        };
      });
      return evidence.data;
    } catch (error) {
      applyDocumentMutationFailure(set, runtime, current, error, 'signature_upload_failed');
      throw error;
    }
  };
  return { uploadDocumentEvidence };
};

type DownloadActions = Pick<
  DocumentActions,
  'downloadDocumentArtifact' | 'downloadDocumentPackage'
>;

const createDocumentDownloadActions = (
  set: SetAdminStore,
  get: GetAdminStore,
  runtime: DocumentRuntime
): DownloadActions => {
  const runDownload = async (download: () => Promise<unknown>) => {
    try {
      await download();
      set({ documentError: null });
    } catch (error) {
      const failure = getAdminApiError(error, 'document_download_failed');
      if (mustClearSensitiveAdminState(error)) {
        clearSensitiveDocumentState(set, runtime);
      }
      set({ documentError: failure });
      throw error;
    }
  };
  const downloadDocumentArtifact: DownloadActions['downloadDocumentArtifact'] = async (
    documentId
  ) => {
    const current = requireCurrentDocument(get);
    await runDownload(() => legalDocumentsApi.downloadDocument(
      current.data.companyId!,
      current.data.id,
      documentId
    ));
  };
  const downloadDocumentPackage: DownloadActions['downloadDocumentPackage'] = async () => {
    const current = requireCurrentDocument(get);
    await runDownload(() => legalDocumentsApi.downloadPackage(
      current.data.companyId!,
      current.data.id
    ));
  };
  return { downloadDocumentArtifact, downloadDocumentPackage };
};

export const createLegalDocumentMutationActions = (
  set: SetAdminStore,
  get: GetAdminStore,
  runtime: DocumentRuntime
): Pick<
  DocumentActions,
  | 'saveLegalProfile'
  | 'refreshLegalProfileConflict'
  | 'executeDocumentCommand'
  | 'refreshDocumentConflict'
  | 'configureDocumentSigners'
  | 'executeDocumentSignerCommand'
  | 'completeDocumentSigner'
  | 'uploadDocumentEvidence'
  | 'downloadDocumentArtifact'
  | 'downloadDocumentPackage'
> => ({
  ...createLegalProfileMutationActions(set, get, runtime),
  ...createPackageCommandActions(set, get, runtime),
  ...createSignerCommandActions(set, get, runtime),
  ...createEvidenceMutationActions(set, get, runtime),
  ...createDocumentDownloadActions(set, get, runtime),
});
